/**
 * Escolher arquivos do Desk Cloud de quem usa, numa grade de cartões: a miniatura (a imagem, o primeiro
 * quadro do vídeo, o ícone do tipo, o play no áudio), o nome, o tipo e o tamanho, com as pastas em
 * cima, a busca no nome e "Carregar mais". Clicar numa pasta abre, e o caminho volta. Clicar num
 * arquivo marca e desmarca, e a marca fica durante a busca e ao trocar de pasta; dois cliques escolhem
 * só aquele. Serve o "Todo o Desk Cloud" do painel Arquivos, que liga os escolhidos à mesa, e o clipe
 * do chat, que os anexa (docs/spec/desk-cloud.md). `hide` tira da lista o que não faz sentido ali (no
 * painel, o que já está na mesa). O corpo (`CloudPickBrowser`) também serve de aba em outros diálogos.
 */
import { useEffect, useState } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import type { DeskFile } from '../shared.js';
import { Button, Dialog, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { FolderFace } from './CloudItems.js';
import { KIND_COLORS, splitName } from './cloudModel.js';
import { FileThumb } from './FileThumb.js';
import { useListen } from './listen.js';
import { useCloudListing, type CloudListing } from './useCloudListing.js';

export function CloudFilePicker({
  title,
  action,
  empty,
  hint,
  hide,
  onPick,
  onClose,
}: {
  title: string;
  /** o botão de confirmar ("Ligar", "Anexar") */
  action: string;
  /** a lista vazia, fora de pasta e sem busca */
  empty: string;
  /** uma linha embaixo da grade, sobre o que acontece com os escolhidos */
  hint?: string;
  hide?: (file: DeskFile) => boolean;
  /** recebe os escolhidos, na ordem em que foram marcados; o botão espera a promessa */
  onPick: (files: DeskFile[]) => void | Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const listing = useCloudListing();
  const [chosen, setChosen] = useState<ReadonlyMap<string, DeskFile>>(new Map());
  const [busy, setBusy] = useState(false);
  // o áudio que se ouviu aqui para quando o seletor fecha
  useEffect(() => () => useListen.getState().stop(), []);

  const toggle = (file: DeskFile) =>
    setChosen((current) => {
      const next = new Map(current);
      if (next.has(file.id)) next.delete(file.id);
      else next.set(file.id, file);
      return next;
    });
  const pick = async (files: DeskFile[]) => {
    if (!files.length) return;
    setBusy(true);
    try {
      await onPick(files);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} disabled={!chosen.size} onClick={() => void pick([...chosen.values()])}>
            {chosen.size > 1 ? `${action} (${chosen.size})` : action}
          </Button>
        </>
      }
    >
      <CloudPickBrowser listing={listing} chosen={chosen} onToggle={toggle} onPick={(file) => void pick([file])} empty={empty} hint={hint} hide={hide} />
    </Dialog>
  );
}

/**
 * O corpo do seletor: a busca, o caminho, as pastas e a grade de cartões, com "Carregar mais" e a
 * dica. Fica num diálogo próprio (acima) ou numa aba de outro, como a Desk Cloud de "Instalar
 * plugin…". `chosen` e `onToggle` são de quem usa (um arquivo só ou vários); dois cliques chamam `onPick`.
 */
export function CloudPickBrowser({
  listing,
  chosen,
  onToggle,
  onPick,
  empty,
  hint,
  hide,
  kindLabel,
}: {
  listing: CloudListing;
  chosen: ReadonlyMap<string, DeskFile>;
  onToggle: (file: DeskFile) => void;
  onPick: (file: DeskFile) => void;
  empty: string;
  hint?: string;
  hide?: (file: DeskFile) => boolean;
  /** o nome do tipo no cartão, quando o da lista diz pouco (os pacotes de plugin são "Outro") */
  kindLabel?: (file: DeskFile) => string;
}) {
  const t = useT();
  const kindOf = (file: DeskFile) => kindLabel?.(file) ?? t(`cloud.kind.${file.kind}`);
  const { files, tiles, folderId, q } = listing;
  const shown = files && hide ? files.filter((file) => !hide(file)) : files;
  const search = t('cloud.search', { where: listing.path[listing.path.length - 1]?.name ?? t('cloud.title') });

  return (
    <div className="rd-cloudPick">
      <label className="rd-searchBox rd-cloudPick__search">
        <Icon name="search" size={15} />
        <input type="search" value={listing.query} onChange={(e) => listing.setQuery(e.target.value)} placeholder={search} aria-label={search} />
      </label>
      <CloudPickCrumbs listing={listing} />
      <div className="rd-cloudPick__body">
        <CloudPickFolders listing={listing} />
        {shown === null ? (
          <div className="rd-cloudPick__state">
            <Spinner size={22} />
          </div>
        ) : !shown.length ? (
          // abaixo das pastas, o aviso é uma linha: elas podem ter arquivos
          <p className={tiles.length ? 'rd-cloudPick__note' : 'rd-cloudPick__state'}>{q ? t('cloud.empty.search') : folderId ? t('cloud.pick.emptyFolder') : empty}</p>
        ) : (
          <section aria-label={t('cloud.filesHeading')}>
            {tiles.length ? <h3 className="rd-cloudSection__title">{t(folderId ? 'cloud.filesHeading' : 'cloud.view.all')}</h3> : null}
            <ul className="rd-cloudPick__grid">
              {shown.map((file) => {
                const on = chosen.has(file.id);
                const { stem, ext } = splitName(file.name);
                return (
                  <li key={file.id} className={`rd-cloudPick__item${on ? ' is-on' : ''}`}>
                    <button
                      type="button"
                      className="rd-cloudPick__hit"
                      aria-pressed={on}
                      aria-label={`${file.name}, ${kindOf(file)}, ${formatBytes(file.size)}`}
                      title={file.name}
                      onClick={() => onToggle(file)}
                      onDoubleClick={() => onPick(file)}
                    />
                    <div className="rd-cloudPick__media">
                      <FileThumb file={file} size="card" />
                      <span className="rd-cloudPick__check" aria-hidden="true">
                        {on ? <Icon name="check" size={13} strokeWidth={3} /> : null}
                      </span>
                    </div>
                    <div className="rd-cloudPick__name">
                      {stem}
                      {ext ? <span className="rd-cloudPick__ext">.{ext}</span> : null}
                    </div>
                    <div className="rd-cloudPick__meta">
                      {/* com outro nome de tipo, a cor do tipo do Cloud não vale */}
                      {kindLabel ? null : <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />}
                      <span className="rd-cloudPick__metaText">
                        {kindOf(file)} · {formatBytes(file.size)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
      {listing.hasMore ? (
        <Button variant="secondary" className="rd-cloudPick__more" busy={listing.loadingMore} onClick={listing.more}>
          {t('cloud.loadMore')}
        </Button>
      ) : null}
      {hint ? <p className="rd-cloudPick__hint">{hint}</p> : null}
    </div>
  );
}

/**
 * O caminho até a pasta aberta: o Desk Cloud e as pastas de cima voltam para lá. Fica também fora de
 * pasta, para o diálogo não mudar de altura ao entrar numa; sem pasta nenhuma, não aparece.
 */
export function CloudPickCrumbs({ listing }: { listing: CloudListing }) {
  const t = useT();
  if (!listing.folders.length) return null;
  const last = listing.path.length - 1;
  return (
    <nav className="rd-cloudCrumbs rd-cloudPick__crumbs" aria-label={t('cloud.where')}>
      <span className="rd-cloudCrumbs__part">
        {listing.folderId ? (
          <button type="button" className="rd-cloudCrumbs__link" onClick={() => listing.open(null)}>
            {t('cloud.title')}
          </button>
        ) : (
          <span className="rd-cloudPick__here" aria-current="location">
            {t('cloud.title')}
          </span>
        )}
      </span>
      {listing.path.map((folder, i) => (
        <span key={folder.id} className="rd-cloudCrumbs__part">
          <Icon name="chevronRight" size={12} />
          {i < last ? (
            <button type="button" className="rd-cloudCrumbs__link" onClick={() => listing.open(folder.id)}>
              {folder.name}
            </button>
          ) : (
            <span className="rd-cloudPick__here" aria-current="location">
              {folder.name}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** As pastas em cima da lista, como na página: clicar abre. */
export function CloudPickFolders({ listing }: { listing: CloudListing }) {
  const t = useT();
  if (!listing.tiles.length) return null;
  return (
    <section aria-label={t('cloud.folders')}>
      <h3 className="rd-cloudSection__title">{t('cloud.folders')}</h3>
      <div className="rd-cloudFolders rd-cloudPick__folders">
        {listing.tiles.map((folder) => (
          <button key={folder.id} type="button" className="rd-cloudFolder" onClick={() => listing.open(folder.id)}>
            <FolderFace folder={folder} subfolders={listing.folders.filter((f) => f.parentId === folder.id).length} />
          </button>
        ))}
      </div>
    </section>
  );
}
